import { Route, Switch } from 'react-router-dom';
import ToggleMigratedDomain from 'App/components/Migrations/ToggleMigratedDomain';
import { HideNavTabs } from 'App/components/Navtabs';
import { MIGRATION_ORDERS_EXPORT_FORCE } from 'App/featuresFlags';
import {
  useGetTags,
  useHasPermission,
  useIsMobileDevice,
  useHasUnifiedShippingDocumentsEnabled,
} from 'domains/Auth/hooks';
import { FULFILLMENT_ORDERS_ROUTES } from 'domains/FulfillmentOrders/fulfillmentOrdersRoutes';
import PrePrintOcaLabelPage from 'domains/FulfillmentOrders/pages/PrePrintOcaDocumentPage/PrePrintOcaLabelPage';
import PrintDocumentPage from 'domains/FulfillmentOrders/pages/PrintDocumentPage';
import { DocumentType } from 'domains/FulfillmentOrders/pages/PrintDocumentPage/constants';
import { useIsEditOrdersEnabled } from './hooks';
import { useIsOrdersAutoCancelEnabled } from './hooks/useIsOrdersAutoCancelEnabled';
import { ORDERS_SECTION_ROUTES } from './ordersSectionRoutes';
import OrderDetailsPage from './pages/OrderDetailsPage';
import OrderEditPage from './pages/OrderEditPage';
import OrderListPage from './pages/OrderListPage';
import OrdersAutoCancelPage from './pages/OrdersAutoCancelPage/OrdersAutoCancelPage';
import OrdersExportPage from './pages/OrdersExportPage';
import RefundOrderPage from './pages/RefundOrderPage';

function Orders(): JSX.Element {
  const isMobileDevice = useIsMobileDevice();
  const isOrderEditEnabled = useIsEditOrdersEnabled();
  const tags = useGetTags();
  const hasOrdersExportForceTag = tags.includes(MIGRATION_ORDERS_EXPORT_FORCE);
  const canExportOrdersCsv = useHasPermission('export_orders_csv');
  const isOrdersAutoCancelEnabled = useIsOrdersAutoCancelEnabled();
  const isUnifiedShippingDocumentsEnabled =
    useHasUnifiedShippingDocumentsEnabled();

  return (
    <>
      <Route exact path={ORDERS_SECTION_ROUTES.orderList}>
        {isMobileDevice ? (
          <OrderListPage />
        ) : (
          <ToggleMigratedDomain domain="orders">
            <OrderListPage />
          </ToggleMigratedDomain>
        )}
      </Route>
      <Switch>
        <Route path={FULFILLMENT_ORDERS_ROUTES.label}>
          <PrintDocumentPage type={DocumentType.LABEL} />
        </Route>
        {isUnifiedShippingDocumentsEnabled ? (
          <Route path={FULFILLMENT_ORDERS_ROUTES.shippingDocument}>
            <PrintDocumentPage type={DocumentType.SHIPPING_DOCUMENT} />
          </Route>
        ) : (
          <Route path={FULFILLMENT_ORDERS_ROUTES.receipt}>
            <PrintDocumentPage type={DocumentType.RECEIPT} />
          </Route>
        )}
        <Route path={FULFILLMENT_ORDERS_ROUTES.contentDeclaration}>
          <PrintDocumentPage type={DocumentType.CONTENT_DECLARATION} />
        </Route>
        <Route path={FULFILLMENT_ORDERS_ROUTES.prePrintOca}>
          <PrePrintOcaLabelPage />
        </Route>
        {isOrdersAutoCancelEnabled && (
          <Route path={ORDERS_SECTION_ROUTES.autoCancel}>
            <OrdersAutoCancelPage />
          </Route>
        )}
        {canExportOrdersCsv && (
          <Route exact path={ORDERS_SECTION_ROUTES.ordersExport}>
            {isMobileDevice ? (
              <OrdersExportPage />
            ) : (
              <ToggleMigratedDomain
                domain="ordersexport"
                force={hasOrdersExportForceTag}
              >
                <OrdersExportPage />
              </ToggleMigratedDomain>
            )}
          </Route>
        )}
        {isOrderEditEnabled && (
          <Route path={ORDERS_SECTION_ROUTES.orderEdit}>
            <HideNavTabs />
            {isMobileDevice ? (
              <OrderEditPage />
            ) : (
              <>
                <HideNavTabs />
                <OrderEditPage />
              </>
            )}
          </Route>
        )}
        <Route path={ORDERS_SECTION_ROUTES.refundOrder}>
          <HideNavTabs />
          <RefundOrderPage />
        </Route>

        <Route path={ORDERS_SECTION_ROUTES.orderDetails}>
          <HideNavTabs />
          {isMobileDevice ? (
            <OrderDetailsPage />
          ) : (
            <ToggleMigratedDomain domain="orders-detail">
              <>
                <HideNavTabs />
                <OrderDetailsPage />
              </>
            </ToggleMigratedDomain>
          )}
        </Route>
      </Switch>
    </>
  );
}

export default Orders;
